import { ComparableColumnFilterRemote, ComparableListFacets } from "src/app/core/valuation-process/_services/valuation-comparable.service";
import { Column, COLUMNS } from "./columns";

export interface ColumnFilter extends Column {
  values: any[],
  selectedValues: any[],
  /** The server had more distinct values than it will list; `values` is the capped set. */
  truncated?: boolean,
  // number columns (e.g. BUA): inclusive low/high bounds, null = unbounded.
  range?: { min: number | null, max: number | null },
  // date columns: narrow to a month (1-12) and/or a year, null = any.
  month?: number | null,
  year?: number | null
}

export function isEqual(a: any, b: any): boolean {
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime()
  }
  return a === b
}

/**
 * A Set-comparable key with the same identity rules as isEqual: dates match on their
 * instant, everything else on strict equality. Lets the value lists — which run to
 * thousands of entries on a large tenant — be looked up in O(1) instead of scanned.
 */
export function valueKey(value: any): any {
  return value instanceof Date ? 'date:' + value.getTime() : value
}

export function keySet(values: any[]): Set<any> {
  const set = new Set<any>()
  for (const value of values) {
    set.add(valueKey(value))
  }
  return set
}

/**
 * Rebuilds the selectable values from the server's facets while keeping what the user
 * has already unticked. Values that appear for the first time start selected.
 */
export function buildFilters(facets: ComparableListFacets, previous: ColumnFilter[]): ColumnFilter[] {
  return COLUMNS.map(column => {
    const facet = facets[column.key]
    const values = (facet?.values ?? []).map(value => fromFacetValue(value, column.type))
    const prev = previous.find(filter => filter.key == column.key)
    let selectedValues = values
    if (prev) {
      const known = keySet(prev.values)
      const ticked = keySet(prev.selectedValues)
      selectedValues = values.filter(value => {
        const key = valueKey(value)
        // Values the user has already seen keep their tick state; new ones start ticked.
        return known.has(key) ? ticked.has(key) : true
      })
    }
    return {
      ...column,
      values,
      selectedValues,
      truncated: facet?.truncated == true,
      // Carry the range / month / year across rebuilds (the value lists can change).
      range: prev?.range ?? { min: null, max: null },
      month: prev?.month ?? null,
      year: prev?.year ?? null
    }
  })
}

/** The filters as the server wants them; columns that restrict nothing are left out. */
export function toRemoteFilters(filters: ColumnFilter[]): ComparableColumnFilterRemote[] {
  const remote: ComparableColumnFilterRemote[] = []

  for (const filter of filters) {
    const column: ComparableColumnFilterRemote = { key: filter.key }
    let restricts = false

    if (filter.selectedValues.length != filter.values.length) {
      const ticked = keySet(filter.selectedValues)
      const excluded = filter.values.filter(value => !ticked.has(valueKey(value)))
      // Send whichever list is shorter — a column with two values unticked out of four
      // thousand must not put four thousand bindings on the query.
      const useInclude = filter.selectedValues.length <= excluded.length
      column.mode = useInclude ? 'include' : 'exclude'
      column.values = (useInclude ? filter.selectedValues : excluded).map(value => toFacetValue(value, filter.type))
      restricts = true
    }

    if (filter.type == 'number' && filter.range && (filter.range.min != null || filter.range.max != null)) {
      column.range = filter.range
      restricts = true
    }

    if (filter.type == 'date' && (filter.month != null || filter.year != null)) {
      column.month = filter.month
      column.year = filter.year
      restricts = true
    }

    if (restricts) {
      remote.push(column)
    }
  }

  return remote
}

/** Dates arrive as `Y-m-d`; the dialog and the table rows both work in Date. */
function fromFacetValue(value: any, type: Column['type']): any {
  if (type != 'date' || value == null) {
    return value
  }
  return new Date(value)
}

/** The inverse — the server matches its own `Y-m-d` strings, not an ISO instant. */
function toFacetValue(value: any, type: Column['type']): any {
  if (type != 'date' || value == null) {
    return value
  }
  const date = value instanceof Date ? value : new Date(value)
  return isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10)
}
